import { aiEditorUrl } from '@dashboard/context/AIContext/aiEditorRoute';
import { hasOneTimePrice, hasRecurringPrice } from '@dashboard/services/getBillingModel';

export const EDITOR_TAB_LABELS = {
  product: 'Product',
  event: 'Event',
  'order-bumps': 'Order bumps',
  'add-ons': 'Add-ons',
  form: 'Details',
  fields: 'Form fields',
  checkout: 'Checkout',
  'after-payment': 'After payment',
  integrations: 'Integrations',
  settings: 'Settings',
  design: 'Theme',
};

const FIRST_TAB_BY_PAGE_TYPE = { checkout: 'product', event: 'event', form: 'fields' };
const DETAILS_TAB_BY_PAGE_TYPE = { checkout: 'product', event: 'event', form: 'form' };

// The tab the editor opens on when the URL names none.
export function firstEditorTab(pageType) {
  return FIRST_TAB_BY_PAGE_TYPE[pageType];
}

// The tab holding the page's description, images and files.
export function detailsEditorTab(pageType) {
  return DETAILS_TAB_BY_PAGE_TYPE[pageType];
}

// Keeps the rest of the query (e.g. share=1) and drops `ai`, which only picks the landing screen.
export function editorLandingLocation({ pageId, pageType, search, opensOnAI = false }) {
  const params = new URLSearchParams(search);
  params.delete('ai');
  const rest = params.toString();
  return {
    pathname: opensOnAI
      ? aiEditorUrl(pageId)
      : `/pages/${pageId}/customize/${firstEditorTab(pageType)}`,
    search: rest ? `?${rest}` : '',
  };
}

export function editorTabLabel(tab, pageType) {
  if (tab === 'after-payment' && pageType === 'form') return 'After submission';
  if (tab === 'fields' && pageType === 'form') return 'Form';
  return EDITOR_TAB_LABELS[tab];
}

const always = () => true;

// Legacy products without a prices[] array are one-time when the page's
// productType is 'charge' (not product.type, unlike hasOneTimePrice).
const productHasOneTimePrice = page => {
  const enabledPrices = (page.product?.prices || []).filter(p => p.enabled !== false);
  return enabledPrices.length > 0
    ? enabledPrices.some(p => p.billingType === 'one_time')
    : page.productType === 'charge';
};

// Checkout-only: event pages also use the Checkout tab with no page.product,
// and hasOneTimePrice(undefined) falls through the legacy fallback to true.
const showPurchaseLimits = page =>
  (hasOneTimePrice(page.product) || hasRecurringPrice(page.product)) && page.type === 'checkout';

export const EDITOR_SECTIONS = [
  { tab: 'product', path: '/details', label: 'Details', isVisible: always },
  { tab: 'product', path: '/description', label: 'Description', isVisible: always },
  { tab: 'product', path: '/image-gallery', label: 'Image gallery', isVisible: always },
  { tab: 'product', path: '/variants', label: 'Variants', isVisible: always },
  { tab: 'product', path: '/file-downloads', label: 'File downloads', isVisible: always },
  { tab: 'product', path: '/inventory', label: 'Inventory', isVisible: always },
  { tab: 'product', path: '/license-keys', label: 'License keys', isVisible: always },
  {
    tab: 'product',
    path: '/bulk-discounts',
    label: 'Bulk discounts',
    isVisible: ({ page }) => productHasOneTimePrice(page),
  },

  // A screen of its own, so its one section has no path of its own.
  { tab: 'order-bumps', path: '', label: 'Order bumps', isVisible: always },

  { tab: 'event', path: '/details', label: 'Details', isVisible: always },
  { tab: 'event', path: '/description', label: 'Description', isVisible: always },
  { tab: 'event', path: '/image-gallery', label: 'Image gallery', isVisible: always },
  { tab: 'event', path: '/ticket-types', label: 'Ticket types', isVisible: always },
  { tab: 'event', path: '/bulk-discounts', label: 'Bulk discounts', isVisible: always },

  { tab: 'add-ons', path: '', label: 'Add-ons', isVisible: always },

  { tab: 'form', path: '/description', label: 'Description', isVisible: always },
  { tab: 'form', path: '/image-gallery', label: 'Image gallery', isVisible: always },
  { tab: 'form', path: '/file-downloads', label: 'File downloads', isVisible: always },

  {
    tab: 'fields',
    path: '/form-fields',
    label: page =>
      page.type === 'event' ? 'Booking questions' : editorTabLabel('fields', page.type),
    isVisible: always,
  },
  {
    tab: 'fields',
    path: '/attendee-questions',
    label: 'Attendee questions',
    isVisible: ({ page }) => page.type === 'event',
  },

  {
    tab: 'checkout',
    path: '/manual-payments',
    label: 'Manual payments',
    isVisible: ({ page }) => hasOneTimePrice(page.product),
  },
  { tab: 'checkout', path: '/payment-methods', label: 'Payment methods', isVisible: always },
  { tab: 'checkout', path: '/fees-surchages', label: 'Additional fees', isVisible: always },
  { tab: 'checkout', path: '/tax', label: 'Tax', isVisible: always },
  {
    tab: 'checkout',
    path: '/cover-processing-fees',
    label: 'Cover processing fees',
    isVisible: always,
  },
  { tab: 'checkout', path: '/abandonment', label: 'Cart abandonment', isVisible: always },

  {
    tab: 'after-payment',
    path: '/confirmation',
    label: page => (page.type === 'form' ? 'After submission' : 'Payment confirmation'),
    isVisible: always,
  },
  {
    tab: 'after-payment',
    path: '/email-confirmation',
    label: 'Email confirmation',
    isVisible: always,
  },
  {
    tab: 'after-payment',
    path: '/one-click-upsell',
    label: 'One-click upsell',
    isVisible: ({ page }) => page.type === 'checkout',
  },

  { tab: 'integrations', path: '/integrations', label: 'Integrations', isVisible: always },

  { tab: 'settings', path: '/url-slug', label: 'URL slug', isVisible: always },
  {
    tab: 'settings',
    path: '/google-index',
    label: 'Search visibility',
    isVisible: ({ seller }) => Boolean(seller?.customDomain && seller?.customDomainVerified),
  },
  { tab: 'settings', path: '/redirect-page', label: 'Page redirect', isVisible: always },
  {
    tab: 'settings',
    path: '/limits',
    label: page =>
      ({ event: 'Repeat registrations', form: 'Repeat submissions' })[page.type] ??
      'Repeat purchases',
    isVisible: ({ page }) =>
      showPurchaseLimits(page) || page.type === 'event' || page.type === 'form',
  },
  {
    tab: 'settings',
    path: '/email-notifications',
    label: 'Admin notifications',
    isVisible: always,
  },
  {
    tab: 'settings',
    path: '/ticket-qr-codes',
    label: 'Ticket QR codes',
    isVisible: ({ page }) => page.type === 'event',
  },
  {
    tab: 'settings',
    path: '/invoices',
    label: 'Invoices',
    isVisible: ({ page }) => page.type === 'checkout' || page.type === 'event',
  },
  { tab: 'settings', path: '/tracking-pixels', label: 'Tracking pixels', isVisible: always },
  {
    tab: 'settings',
    path: '/dynamic-info',
    label: page => (page.type === 'checkout' ? 'Dynamic price' : 'Dynamic info'),
    isVisible: always,
  },
  { tab: 'settings', path: '/pop-up-behavior', label: 'Pop-up behavior', isVisible: always },

  { tab: 'design', path: '/theme', label: 'Theme', isVisible: always },
];

const resolveSection = (section, page) => ({
  ...section,
  label: typeof section.label === 'function' ? section.label(page) : section.label,
});

export function editorSections(tab, { page, seller }) {
  return EDITOR_SECTIONS.filter(
    section => section.tab === tab && section.isVisible({ page, seller }),
  ).map(section => resolveSection(section, page));
}

export function editorSection(tab, path, { page }) {
  const section = EDITOR_SECTIONS.find(s => s.tab === tab && s.path === path);
  return section && resolveSection(section, page);
}
